/** Лента оценки интерьеров: клиент `/api/interiors/*` (спека §5). Логика очереди — `interiors.ts`. */

import { ApiFailure, apiFetch, query } from "@/lib/api";
import { loginPath } from "@/lib/loginReturn";
import type { InteriorMe, InteriorNext, InteriorStats, Verdict } from "./interiors";

/**
 * Потолок ожидания ответа ленты. На телефоне зависший запрос иначе держит цепочку голосов
 * вечно, и голоса теряются молча; по таймауту запрос падает в обычный путь «не сохранилось».
 */
const REQUEST_TIMEOUT_MS = 15000;

let redirecting = false;

/** 401 посреди сессии — на вход (один раз, без цикла); ошибка летит дальше как была. */
function guard<T>(p: Promise<T>): Promise<T> {
  return p.catch((err) => {
    if (err instanceof ApiFailure && err.status === 401 && !redirecting) {
      redirecting = true;
      window.location.assign(loginPath());
    }
    throw err;
  });
}

export function loadMe(): Promise<InteriorMe> {
  return apiFetch<InteriorMe>("/api/interiors/me");
}

export function ackInstruction(): Promise<unknown> {
  return apiFetch<unknown>("/api/interiors/ack", { method: "POST", body: "{}" });
}

export function loadNext(n: number, afterTag: number | null, exclude: number[]): Promise<InteriorNext> {
  const qs = query({ n, after_tag: afterTag, exclude: exclude.length ? exclude.join(",") : null });
  return guard(apiFetch<InteriorNext>(`/api/interiors/next${qs}`, { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) }));
}

export function sendVote(referenceItemId: number, verdict: Verdict): Promise<{ voted: number }> {
  return guard(
    apiFetch<{ voted: number }>("/api/interiors/vote", {
      method: "POST",
      body: JSON.stringify({ reference_item_id: referenceItemId, verdict }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    }),
  );
}

export function removeVote(referenceItemId: number): Promise<{ voted: number }> {
  return guard(
    apiFetch<{ voted: number }>(`/api/interiors/vote/${referenceItemId}`, {
      method: "DELETE",
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    }),
  );
}

export function loadStats(userId?: number): Promise<InteriorStats> {
  return apiFetch<InteriorStats>(`/api/interiors/stats${query({ user_id: userId })}`);
}
